import React, { useState } from 'react';
import {
  Card,
  InlineStack,
  BlockStack,
  Text,
  Badge,
  Icon,
  Collapsible,
  Box,
} from '@shopify/polaris';
import {
  CheckCircleIcon,
  XCircleIcon,
} from '@shopify/polaris-icons';
import { COMPLIANCE_CHECK_LABELS, SEVERITY_TONES, type SeverityLevel } from '../theme';

/**
 * T091: ComplianceChecklist component.
 * 10 check items with pass/fail icon, severity badge,
 * expandable remediation text.
 * Uses theme config for labels and severity tone mapping.
 *
 * @see documents/ui-enhancement-spec.md §10.6
 */

interface ComplianceCheckResult {
  checkType: string;
  passed: boolean;
  severity: string;
  remediationText: string | null;
}

interface ComplianceChecklistProps {
  results: ComplianceCheckResult[];
}

const CHECK_LABELS = COMPLIANCE_CHECK_LABELS;

function severityTone(severity: string) {
  return (SEVERITY_TONES[severity as SeverityLevel] ?? 'info') as 'critical' | 'warning' | 'attention' | 'info';
}

function CheckItem({ result }: { result: ComplianceCheckResult }) {
  const [expanded, setExpanded] = useState(false);
  const label = CHECK_LABELS[result.checkType] ?? result.checkType;

  return (
    <Box paddingBlockStart="200" paddingBlockEnd="200">
      <BlockStack gap="100">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Icon
              source={result.passed ? CheckCircleIcon : XCircleIcon}
              tone={result.passed ? 'success' : 'critical'}
            />
            <Text
              as="span"
              variant="bodyMd"
              fontWeight={result.passed ? 'regular' : 'semibold'}
            >
              {label}
            </Text>
          </InlineStack>
          <InlineStack gap="200" blockAlign="center">
            <Badge tone={severityTone(result.severity)}>{result.severity}</Badge>
            {!result.passed && result.remediationText && (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--p-color-text-interactive)',
                  fontSize: '0.8125rem',
                  padding: 0,
                }}
              >
                {expanded ? 'Hide fix' : 'How to fix'}
              </button>
            )}
          </InlineStack>
        </InlineStack>

        {!result.passed && result.remediationText && (
          <Collapsible
            id={`remediation-${result.checkType}`}
            open={expanded}
            transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}
          >
            <Box
              paddingInlineStart="800"
              paddingBlockStart="100"
              paddingBlockEnd="100"
            >
              <Text as="p" variant="bodySm" tone="subdued">
                {result.remediationText}
              </Text>
            </Box>
          </Collapsible>
        )}
      </BlockStack>
    </Box>
  );
}

export function ComplianceChecklist({ results }: ComplianceChecklistProps): React.ReactElement {
  return (
    <Card>
      <BlockStack gap="0">
        <Box paddingBlockEnd="200">
          <Text as="h2" variant="headingMd">Compliance Checks</Text>
        </Box>
        {results.map((result) => (
          <CheckItem key={result.checkType} result={result} />
        ))}
      </BlockStack>
    </Card>
  );
}
